import type { ChatIntegrationProvider, IChatIntegrations } from '@interfaces/IChatIntegrations';
import { authedFetch, BFF_BASE_URL, UnauthorizedError } from '@apis/auth';

/** The caller's chat integrations: every app, and their connection to each. */
export async function fetchChatIntegrations(signal?: AbortSignal): Promise<IChatIntegrations> {
  const res = await authedFetch('/api/me/chat-integrations', { signal });
  if (res.ok) return (await res.json()) as IChatIntegrations;
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`fetch chat integrations failed: ${res.status}`);
}

/** Switch the caller's notes in one app on or off. */
export async function setAppEnabled(provider: ChatIntegrationProvider, enabled: boolean): Promise<void> {
  const res = await authedFetch(`/api/me/chat-integrations/${provider}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  });
  if (res.ok) return;
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`switch chat integration failed: ${res.status}`);
}

/** Disconnect the caller from one app. */
export async function disconnectApp(provider: ChatIntegrationProvider): Promise<void> {
  const res = await authedFetch(`/api/me/chat-integrations/${provider}`, { method: 'DELETE' });
  if (res.ok) return;
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`disconnect chat integration failed: ${res.status}`);
}

/**
 * Where to NAVIGATE (not fetch) to connect an app. The BFF holds the connect's
 * state in a cookie and sends the browser to the app's approval; the app sends
 * it back to the BFF, which lands on `returnTo` with `?connected=<app>` or
 * `?connectError=<app>`.
 */
export function connectStartUrl(provider: ChatIntegrationProvider, returnTo: string): string {
  const query = new URLSearchParams({ returnTo });
  return `${BFF_BASE_URL}/api/me/chat-integrations/${provider}/start?${query.toString()}`;
}

/** Leaves for the app's approval (see `connectStartUrl`), coming back to this page. */
export function startConnect(provider: ChatIntegrationProvider): void {
  window.location.assign(connectStartUrl(provider, window.location.pathname + window.location.search));
}
